import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import { Project } from '../Components/Projects/projects';
import type { DriveUnitPortOccupancy } from '../redux/ApplicationSlice';
import { toPersistableDashboardLayout } from './dashboardWidgetUtils';
import { buildPersistableDashboardContextForProject } from './persistableDashboardProjectUtils';
import { checkForUnsupportedCharacters } from './helpers';

export function uniqueProjectsById(projects: Project[]): Project[] {
  const byId = new Map<string, Project>();
  projects.forEach((project) => {
    byId.set(project.id, project);
  });
  return Array.from(byId.values());
}

/** Safe `.bps` filename for single-file download and zip entries. */
export function projectBpsDownloadFilename(projectName: string): string {
  const title = checkForUnsupportedCharacters(projectName);
  return `${title}.bps`;
}

/** Zip entry name; disambiguates when multiple projects sanitize to the same `.bps` name. */
export function uniqueProjectBpsZipEntryFilename(projectName: string, projectId: string, usedEntryNames: Set<string>): string {
  const base = projectBpsDownloadFilename(projectName);
  if (!usedEntryNames.has(base)) {
    usedEntryNames.add(base);
    return base;
  }

  const stem = base.slice(0, -'.bps'.length);
  const idSuffix = checkForUnsupportedCharacters(projectId);
  let counter = 1;
  let candidate = `${stem} (${idSuffix}).bps`;
  while (usedEntryNames.has(candidate)) {
    counter += 1;
    candidate = `${stem} (${idSuffix} ${counter}).bps`;
  }
  usedEntryNames.add(candidate);
  return candidate;
}

function projectForBpsDownload(project: Project, driveUnitPorts?: DriveUnitPortOccupancy[]): Project {
  const persistContext = buildPersistableDashboardContextForProject(project, driveUnitPorts);
  return {
    ...project,
    dashboardLayout: toPersistableDashboardLayout(project.dashboardLayout ?? [], persistContext),
  };
}

/** Downloads one project as a `.bps` file. */
export function downloadProjectAsBpsFile(project: Project, driveUnitPorts: DriveUnitPortOccupancy[] = []): void {
  const jsonString = JSON.stringify(projectForBpsDownload(project, driveUnitPorts), null, 2);
  const blob = new Blob([jsonString], { type: 'application/json;charset=utf-8' });
  saveAs(blob, projectBpsDownloadFilename(project.name));
}

export type DriveUnitPortsResolver = (project: Project) => DriveUnitPortOccupancy[];

/** Downloads one or more projects (zip when multiple). */
export async function downloadProjects(projects: Project[], resolveDriveUnitPorts: DriveUnitPortsResolver = () => []): Promise<void> {
  const uniqueProjects = uniqueProjectsById(projects);
  if (uniqueProjects.length === 0) {
    return;
  }

  if (uniqueProjects.length === 1) {
    downloadProjectAsBpsFile(uniqueProjects[0], resolveDriveUnitPorts(uniqueProjects[0]));
    return;
  }

  const zip = new JSZip();
  const usedEntryNames = new Set<string>();
  uniqueProjects.forEach((project) => {
    const jsonString = JSON.stringify(projectForBpsDownload(project, resolveDriveUnitPorts(project)), null, 2);
    zip.file(uniqueProjectBpsZipEntryFilename(project.name, project.id, usedEntryNames), jsonString);
  });

  const content = await zip.generateAsync({ type: 'blob' });
  saveAs(content, 'projects.zip');
}
